import { useQuery } from '@tanstack/react-query';
import { useShellSearchParams } from '../lib/use-shell-search-params';
import { useLocale } from '../i18n/locale-context';
import {
  fetchIfiSourceCounts,
  parseIfiSourceParam,
  parseKindParam,
  type IfiSourceKey,
  parseStatusParam,
  parseIntParam,
  parseIsoDateParam,
} from '../lib/catalog-client';
import { TabStrip, type TabStripItem } from './tab-strip';
import { countryConfig } from '../country';
import type { PublicCountryConfig } from '@tenders/shared';

/** Exported so a test can pin this literal list to the real
 *  `IFI_SOURCE_KEYS` from catalog-client (#53) — the same guard the server
 *  runs against `ifi-groups.ts`'s `GROUPS`, so a new institution can't
 *  silently filter on the API while having no chip here. */
export const TABS: Array<{ value: '' | IfiSourceKey; labelKey: string }> = [
  { value: '', labelKey: 'ifi.source.all' },
  { value: 'worldbank', labelKey: 'ifi.source.worldbank' },
  { value: 'adb', labelKey: 'ifi.source.adb' },
  { value: 'ungm', labelKey: 'ifi.source.ungm' },
  { value: 'undp', labelKey: 'ifi.source.undp' },
  { value: 'aiib', labelKey: 'ifi.source.aiib' },
  { value: 'isdb', labelKey: 'ifi.source.isdb' },
  { value: 'ebrd', labelKey: 'ifi.source.ebrd' },
  { value: 'uzrd', labelKey: 'ifi.source.uzrd' },
  { value: 'osce', labelKey: 'ifi.source.osce' },
];

/**
 * The chips THIS deployment can fill: «Все» plus every institution in the
 * country's `ifiSources`, in the order of {@link TABS}. Uzbekistan lists all of
 * them, so its strip is unchanged; kaztender.com/ifi used to offer «UzRD» and
 * «ОБСЕ», Uzbek-only sources that could only ever read 0 (audit 2026-10-16).
 * A list of zero or one institution draws no strip at all — a single chip
 * next to «Все» filters to the same lots.
 */
export function visibleIfiTabs(
  config: Pick<PublicCountryConfig, 'ifiSources'> = countryConfig,
): typeof TABS {
  const tabs = TABS.filter((tab) => tab.value === '' || config.ifiSources.includes(tab.value));
  return tabs.length > 2 ? tabs : [];
}

const VISIBLE_TABS = visibleIfiTabs();

/**
 * Чипы институтов на /ifi: Все · Всемирный банк · АБР · UNGM · ПРООН · AIIB (#53).
 * Управляют параметром `ifiSource` в URL — всё состояние в адресной строке, как
 * у остальных фильтров.
 *
 * Счётчики обязательны, а не декоративны: слой перекошен (UNGM — две трети
 * живых лотов, AIIB — два), и без числа пользователь жмёт чип, видит почти
 * пустой список и решает, что раздел сломан.
 *
 * Остаётся видимой и во время текстового поиска: `GET /catalog/search` с #53
 * умеет фильтр по источнику (скалярный подзапрос на `tender_sources` во всех
 * трёх построителях), так что активный чип там действительно фильтрует. А вот
 * счётчики в этом режиме не запрашиваются и не рисуются — точного числа для
 * ранжированной по релевантности выдачи не существует.
 */
export function IfiSourceTabs() {
  const { t } = useLocale();
  const [params, setParams] = useShellSearchParams();
  const active = parseIfiSourceParam(params.get('ifiSource')) ?? '';

  const region = params.get('region') ?? undefined;
  const category = params.get('category') ?? undefined;
  const ikpu = params.get('ikpu') ?? undefined;
  const niche = params.get('niche') ?? undefined;
  const status = parseStatusParam(params.get('status'));
  const q = params.get('q') ?? undefined;
  // Guarded exactly like `kind` above: an unrecognised value is "no filter",
  // never passed through to 400 the counts request.
  const minBudgetRaw = parseIntParam(params.get('minBudget'));
  const maxBudgetRaw = parseIntParam(params.get('maxBudget'));
  const deadlineFrom = parseIsoDateParam(params.get('deadlineFrom'));
  const deadlineTo = parseIsoDateParam(params.get('deadlineTo'));
  const since = parseIsoDateParam(params.get('since'));
  // Same guard as catalog.tsx/kind-tabs.tsx: `kind` comes off a user-editable
  // URL param, so an unvalidated cast could send `?kind=garbage` through to
  // the counts request and 400 it instead of degrading to "no filter".
  const kind = parseKindParam(params.get('kind'));
  const minBudget = minBudgetRaw !== undefined ? Number(minBudgetRaw) : undefined;
  const maxBudget = maxBudgetRaw !== undefined ? Number(maxBudgetRaw) : undefined;

  const { data: counts } = useQuery({
    queryKey: [
      'catalog',
      'ifi-source-counts',
      {
        region,
        category,
        ikpu,
        niche,
        status,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        kind,
      },
    ],
    queryFn: () =>
      fetchIfiSourceCounts({
        region,
        category,
        ikpu,
        niche,
        status,
        // Слой фиксирован самой страницей, а не URL — сервер иначе вернёт нули.
        scope: 'ifi',
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        kind,
      }),
    // Точного числа при активном текстовом поиске не существует: выдача
    // ранжируется гибридным семантика+ключевые слова поиском с порогом
    // релевантности, а эндпоинт счётчиков `q` игнорирует. Не запрашиваем вовсе,
    // чтобы не рисовать врущие числа — та же логика, что у KindTabs.
    // Нет полосы — нет и запроса за её числами.
    enabled: !q && VISIBLE_TABS.length > 0,
  });

  function select(value: '' | IfiSourceKey) {
    const next = new URLSearchParams(params);
    if (value) next.set('ifiSource', value);
    else next.delete('ifiSource');
    next.delete('page'); // смена чипа сбрасывает пагинацию
    setParams(next);
  }

  function countFor(value: '' | IfiSourceKey): number | undefined {
    if (!counts) return undefined;
    return value === '' ? counts.all : counts[value];
  }

  if (VISIBLE_TABS.length === 0) return null;

  const items: TabStripItem<'' | IfiSourceKey>[] = VISIBLE_TABS.map((tab) => {
    const count = countFor(tab.value);
    const label = t(tab.labelKey);
    return {
      value: tab.value,
      label,
      count,
      // Число складываем в явный aria-label: сами цифры в TabStrip помечены
      // aria-hidden, а именно они говорят, какой чип пустой. Пока счётчики
      // грузятся — просто метка, без фразы с дырой вместо числа.
      ariaLabel: count != null ? t('ifi.source.ariaLabel', { label, count }) : label,
    };
  });

  return (
    <TabStrip ariaLabel={t('ifi.source.aria')} items={items} active={active} onSelect={select} />
  );
}
